import api from './api';

export interface SystemLogEntry {
  id: string;
  sourceId: string;
  eventType: string;
  message: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  level?: 'SUCCESS' | 'INFO' | 'WARN' | 'ERROR';
  logType?: string;
  domain: string;
  timestamp: string;
  additionalContext: any;
}

export interface SystemLogQuery {
  startDate?: string;
  endDate?: string;
  severity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  level?: 'SUCCESS' | 'INFO' | 'WARN' | 'ERROR';
  domain?: string;
  eventType?: string;
  logType?: string;
  sourceId?: string;
  limit?: number;
  nextToken?: string;
}

export interface SystemLogResponse {
  logs: SystemLogEntry[];
  nextToken?: string;
  total: number;
}

export const systemLogsService = {
  /**
   * Get system logs with filtering and pagination
   */
  getSystemLogs: async (query: SystemLogQuery = {}): Promise<SystemLogResponse> => {
    const params = new URLSearchParams();
    
    if (query.startDate) params.append('startDate', query.startDate);
    if (query.endDate) params.append('endDate', query.endDate);
    if (query.severity) params.append('severity', query.severity);
    if (query.level) params.append('level', query.level);
    if (query.domain) params.append('domain', query.domain);
    if (query.eventType) params.append('eventType', query.eventType);
    if (query.logType) params.append('logType', query.logType);
    if (query.sourceId) params.append('sourceId', query.sourceId);
    if (query.limit) params.append('limit', query.limit.toString());
    if (query.nextToken) params.append('nextToken', query.nextToken);

    const response = await api.get(`/system-logs?${params.toString()}`);
    return response.data;
  },

  /**
   * Get available domains for filtering
   */
  getAvailableDomains: (): string[] => {
    return ['source', 'url', 'property', 'cron'];
  },

  /**
   * Get available severities for filtering
   */
  getAvailableSeverities: (): Array<'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'> => {
    return ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
  },

  getAvailableLevels: (): Array<'SUCCESS' | 'INFO' | 'WARN' | 'ERROR'> => {
    return ['SUCCESS', 'INFO', 'WARN', 'ERROR'];
  },

  /**
   * Get severity color for UI display
   */
  getSeverityColor: (severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'): string => {
    switch (severity) {
      case 'CRITICAL':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'HIGH':
        return 'text-orange-600 bg-orange-50 border-orange-200';
      case 'MEDIUM':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'LOW':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  },

  /**
   * Get severity icon for UI display
   */
  getSeverityIcon: (severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'): string => {
    switch (severity) {
      case 'CRITICAL':
        return '🔴';
      case 'HIGH':
        return '🟠';
      case 'MEDIUM':
        return '🟡';
      case 'LOW':
        return '🔵';
      default:
        return '⚪';
    }
  },

  /**
   * Format timestamp for display
   */
  formatTimestamp: (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  },

  /**
   * Get domain display name
   */
  getDomainDisplayName: (domain: string): string => {
    const domainNames: Record<string, string> = {
      source: 'Source Processing',
      url: 'URL Processing',
      property: 'Property Management',
      cron: 'Scheduled Jobs',
    };
    return domainNames[domain] || domain;
  },
}; 